import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { toast } from "sonner";
import { 
  Target, 
  CheckCircle2, 
  Circle,
  Trophy,
  Star,
  Users,
  DollarSign,
  Share2,
  QrCode,
  User,
  Globe,
  BookOpen,
  TrendingUp,
  Award,
  Sparkles,
  Gift,
  Loader2,
  ChevronRight,
  Lock
} from "lucide-react";
import { API } from "../../apiConfig";


const ICON_MAP = {
  "share": Share2,
  "user": User,
  "qr-code": QrCode,
  "user-plus": Users,
  "dollar-sign": DollarSign,
  "users": Users,
  "globe": Globe,
  "book-open": BookOpen,
  "award": Award,
  "star": Star,
  "trending-up": TrendingUp,
};

const CATEGORY_INFO = {
  onboarding: { title: "Getting Started", color: "bg-blue-500", icon: Sparkles },
  growth: { title: "Growing Your Network", color: "bg-green-500", icon: TrendingUp },
  engagement: { title: "Building Engagement", color: "bg-purple-500", icon: Share2 },
  milestone: { title: "Achievements", color: "bg-amber-500", icon: Trophy },
};

const MissionsHub = ({ token }) => {
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [weeklyChallenge, setWeeklyChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    try {
      const [missionsRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/missions`, { headers }),
        axios.get(`${API}/missions/weekly-challenge`, { headers }).catch(() => null)
      ]);
      setMissions(missionsRes.data.missions || []);
      setStats(missionsRes.data.stats);
      if (weeklyRes?.data) {
        setWeeklyChallenge(weeklyRes.data);
      }
    } catch (error) {
      console.error("Error fetching missions:", error);
    } finally {
      setLoading(false);
    }
  };

  const completeMission = async (missionId) => {
    setCompleting(missionId);
    try {
      const res = await axios.post(`${API}/missions/complete`, 
        { mission_id: missionId }, 
        { headers }
      );
      
      if (res.data.already_completed) {
        toast.info("Mission already completed!");
      } else {
        toast.success(`🎉 Mission complete! +${res.data.points_awarded} points`);
        fetchMissions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete mission");
    } finally {
      setCompleting(null);
    }
  };

  const trackQrDownload = async () => {
    try {
      await axios.post(`${API}/missions/track/download_qr`, {}, { headers });
      fetchMissions();
    } catch (error) {
      // Silent fail
    }
  };

  // Group missions by category
  const missionsByCategory = missions.reduce((acc, mission) => {
    const cat = mission.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(mission);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-amber-500 text-white">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Mission Progress</h3>
                <p className="text-sm text-muted-foreground">
                  Complete missions to earn bonus points
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-amber-600">
                {stats?.completed || 0}/{stats?.total || 0}
              </p>
              <p className="text-sm text-muted-foreground">Missions Complete</p>
            </div>
          </div>
          
          <Progress 
            value={stats?.progress_percent || 0} 
            className="h-3 bg-amber-100"
          />
          
          <div className="flex justify-between mt-2 text-sm">
            <span className="text-amber-700">
              {stats?.points_earned_from_missions || 0} points earned from missions
            </span>
            <span className="text-muted-foreground">
              {stats?.progress_percent || 0}% complete
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Challenge */}
      {weeklyChallenge && (
        <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200 overflow-hidden">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-purple-500 text-white">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Weekly Challenge</h3>
                    <Badge className="bg-purple-100 text-purple-700">
                      {weeklyChallenge.challenge.days_remaining} days left
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {weeklyChallenge.challenge.title}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">
                  +{weeklyChallenge.challenge.points_reward}
                </p>
                <p className="text-sm text-muted-foreground">bonus points</p>
              </div>
            </div>
            
            <div className="mt-4">
              <p className="text-sm mb-2">{weeklyChallenge.challenge.description}</p>
              <div className="flex items-center gap-4">
                <Progress 
                  value={(weeklyChallenge.progress / weeklyChallenge.target) * 100} 
                  className="flex-1 h-3 bg-purple-100"
                />
                <span className="text-sm font-medium">
                  {weeklyChallenge.progress}/{weeklyChallenge.target}
                </span>
              </div>
              {weeklyChallenge.completed && (
                <div className="mt-3 flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Challenge completed!</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mission Categories */}
      <Tabs defaultValue="onboarding" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full">
          {Object.entries(CATEGORY_INFO).map(([key, info]) => {
            const categoryMissions = missionsByCategory[key] || [];
            const completed = categoryMissions.filter(m => m.completed).length;
            return (
              <TabsTrigger key={key} value={key} className="relative">
                <info.icon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{info.title.split(" ")[0]}</span>
                {completed > 0 && (
                  <Badge className="ml-2 bg-green-500 text-white text-xs px-1.5">
                    {completed}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(CATEGORY_INFO).map(([category, info]) => (
          <TabsContent key={category} value={category}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${info.color} text-white`}>
                    <info.icon className="w-5 h-5" />
                  </div>
                  {info.title}
                </CardTitle>
                <CardDescription>
                  {category === "onboarding" && "Get started with these foundational tasks"}
                  {category === "growth" && "Expand your network and earn more"}
                  {category === "engagement" && "Stay active and build your presence"}
                  {category === "milestone" && "Major achievements to unlock"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(missionsByCategory[category] || []).map((mission) => {
                    const IconComponent = ICON_MAP[mission.icon] || Target;
                    const isCompleted = mission.completed;
                    const hasProgress = mission.target > 1;
                    const progressPercent = hasProgress 
                      ? Math.min((mission.progress / mission.target) * 100, 100)
                      : 0;
                    
                    return (
                      <div 
                        key={mission.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isCompleted 
                            ? "bg-green-50 border-green-200" 
                            : "bg-white hover:border-amber-300"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${
                            isCompleted 
                              ? "bg-green-500 text-white" 
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {isCompleted ? (
                              <CheckCircle2 className="w-5 h-5" />
                            ) : (
                              <IconComponent className="w-5 h-5" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-medium ${isCompleted ? "text-green-700" : ""}`}>
                                {mission.title}
                              </h4>
                              <Badge variant="outline" className={`text-xs ${
                                isCompleted ? "border-green-300 text-green-600" : ""
                              }`}>
                                +{mission.points_reward} pts
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {mission.description}
                            </p>
                            
                            {/* Progress bar for multi-target missions */}
                            {hasProgress && !isCompleted && (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs mb-1">
                                  <span>Progress</span>
                                  <span>{mission.progress}/{mission.target}</span>
                                </div>
                                <Progress value={progressPercent} className="h-2" />
                              </div>
                            )}
                          </div>
                          
                          <div>
                            {isCompleted ? (
                              <Badge className="bg-green-500 text-white">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                              </Badge>
                            ) : mission.auto_complete ? (
                              <Badge variant="outline" className="text-muted-foreground">
                                <Lock className="w-3 h-3 mr-1" /> Auto
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => completeMission(mission.id)}
                                disabled={completing === mission.id}
                                className="bg-amber-500 hover:bg-amber-600"
                              >
                                {completing === mission.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    Complete
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {(!missionsByCategory[category] || missionsByCategory[category].length === 0) && (
                    <p className="text-center text-muted-foreground py-8">
                      No missions in this category yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default MissionsHub;
